import React, { useState, useRef, useEffect } from 'react';
import { useAdVocalizeCore, VOICES } from '../context/AdVocalizeCore';
import { MiniStepDashes, PlayPill } from '../components/AppShell';
import { ChevronLeft, RefreshCw, FileAudio, Video, Heart, MessageCircle, Send, Bookmark } from 'lucide-react';

const FILTERS = [
    { id: 'none',      css: 'none' },
    { id: 'vivid',     css: 'saturate(1.5) contrast(1.1)' },
    { id: 'warm',      css: 'sepia(0.45) brightness(1.05) saturate(1.2)' },
    { id: 'cool',      css: 'saturate(0.8) brightness(1.05) hue-rotate(195deg)' },
    { id: 'noir',      css: 'grayscale(1) contrast(1.1)' },
    { id: 'cinematic', css: 'contrast(1.12) saturate(0.78) brightness(0.94)' },
];

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const Step3_MasterTape = () => {
    const {
        image,
        aspectRatio,
        imgTransform,
        selectedFilter,
        audioTakes,
        selectedTakeIdx,
        text,
        primaryMemory,
        setStep,
        setAuthError,
        fulfillExport,
    } = useAdVocalizeCore();

    const [isPlaying, setIsPlaying]           = useState(false);
    const [previewTime, setPreviewTime]       = useState(0);
    const [previewDuration, setPreviewDuration] = useState(0);
    const [exportType, setExportType]         = useState('video');
    const [isRendering, setIsRendering]       = useState(false);
    const [videoReady, setVideoReady]         = useState(null);
    const [previewMode, setPreviewMode]       = useState('social'); // 'social' | 'device'
    const chargedRef                          = useRef(false); // only consume 1 credit per session

    const previewAudioRef = useRef(null);
    const activeTake      = audioTakes[selectedTakeIdx];
    const filterCss       = FILTERS.find(f => f.id === selectedFilter)?.css || 'none';
    const voiceInfo       = VOICES.find(v => v.name === activeTake?.voiceName);
    const voiceLabel      = voiceInfo?.label || activeTake?.voiceLabel || 'Voice';
    const genderLabel     = voiceInfo?.gender === 'male' ? 'Male' : 'Female';
    const isLandscape     = aspectRatio === '16:9' || aspectRatio === '4:3';
    const isStoryFormat   = aspectRatio === '9:16';
    const isFeedFormat    = ['1:1', '4:5'].includes(aspectRatio);
    const brandName       = primaryMemory?.brandName || 'yourbrand';

    const SCRIPT_TYPE_STYLES = {
        soul:  { label: 'SOUL',  cls: 'text-purple-400 bg-purple-500/20 border-purple-500/30' },
        pulse: { label: 'PULSE', cls: 'text-orange-400 bg-orange-500/20 border-orange-500/30' },
        fact:  { label: 'FACT',  cls: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30' },
    };
    const scriptBadge = activeTake?.scriptType ? SCRIPT_TYPE_STYLES[activeTake.scriptType] : null;

    // Wire play / pause to the audio element
    useEffect(() => {
        const audio = previewAudioRef.current;
        if (!audio) return;
        if (isPlaying) {
            audio.play().catch(() => setIsPlaying(false));
        } else {
            audio.pause();
        }
    }, [isPlaying]);

    const handleDownload = async () => {
        if (!activeTake) return;

        // Consume 1 session credit on first download (non-BYOK only)
        if (!chargedRef.current) {
            try {
                await fulfillExport();
                chargedRef.current = true;
            } catch (e) {
                const msg = e?.message || '';
                if (msg.includes('No session credits') || msg.includes('resource-exhausted') || e?.code === 'functions/resource-exhausted') {
                    setAuthError('Free sessions used up. Open Settings (top-right ⚙) to add your Gemini API key for unlimited access.');
                    return;
                }
                // Non-credit errors (network, etc.) — don't block the download
                chargedRef.current = true;
            }
        }

        if (exportType === 'audio') {
            const a = document.createElement('a');
            a.href = activeTake.url;
            a.download = 'AdVocalize_Audio.wav';
            a.click();
            return;
        }

        // Video: re-download if already rendered
        if (videoReady) {
            const a = document.createElement('a');
            a.href = videoReady;
            a.download = 'AdVocalize_Video.webm';
            a.click();
            return;
        }

        // Render video on canvas
        setIsRendering(true);
        try {
            const canvas = document.createElement('canvas');
            const ctx    = canvas.getContext('2d');
            let w = 1080, h = 1080;
            if (aspectRatio === '9:16')  { w = 1080; h = 1920; }
            else if (aspectRatio === '16:9') { w = 1920; h = 1080; }
            else if (aspectRatio === '4:5') { w = 1080; h = 1350; }
            canvas.width = w; canvas.height = h;

            const img = new Image();
            img.src = image;
            await new Promise(res => { img.onload = res; });

            // Decode audio and route to a MediaStream destination so it's mixed into the recording
            const audioCtx  = new AudioContext();
            await audioCtx.resume();
            const audioResp = await fetch(activeTake.url);
            const audioBuffer = await audioCtx.decodeAudioData(await audioResp.arrayBuffer());
            const audioDest  = audioCtx.createMediaStreamDestination();
            const audioSrc   = audioCtx.createBufferSource();
            audioSrc.buffer  = audioBuffer;
            audioSrc.connect(audioDest);
            audioSrc.start(0);

            // Combine canvas video track + decoded audio track
            const canvasStream = canvas.captureStream(30);
            const compositeStream = new MediaStream([
                ...canvasStream.getVideoTracks(),
                ...audioDest.stream.getAudioTracks(),
            ]);

            const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
                ? 'video/webm;codecs=vp9,opus'
                : 'video/webm';
            const recorder = new MediaRecorder(compositeStream, { mimeType });
            const chunks   = [];
            recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                audioCtx.close();
                const blob = new Blob(chunks, { type: 'video/webm' });
                const url  = URL.createObjectURL(blob);
                setVideoReady(url);
                setIsRendering(false);
                const a = document.createElement('a');
                a.href = url; a.download = 'AdVocalize_Video.webm'; a.click();
            };
            recorder.start();

            // Draw loop — object-cover fit (scale to fill, crop excess) + imgTransform + filter
            const coverScale = Math.max(w / img.width, h / img.height);
            const drawW = img.width  * coverScale;
            const drawH = img.height * coverScale;

            const start    = Date.now();
            const duration = audioBuffer.duration * 1000;
            const draw = () => {
                const elapsed = Date.now() - start;
                ctx.fillStyle = '#020617';
                ctx.fillRect(0, 0, w, h);
                ctx.save();
                ctx.filter = filterCss;
                ctx.translate(w / 2 + imgTransform.x, h / 2 + imgTransform.y);
                ctx.scale(imgTransform.scale, imgTransform.scale);
                ctx.rotate((imgTransform.rotate * Math.PI) / 180);
                ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
                ctx.restore();
                if (elapsed < duration) requestAnimationFrame(draw);
                else recorder.stop();
            };
            draw();
        } catch (e) {
            setAuthError('Export failed: ' + e.message);
            setIsRendering(false);
        }
    };

    const progress = previewDuration ? (previewTime / previewDuration) * 100 : 0;

    return (
        <div className="max-w-7xl mx-auto px-8 py-8 animate-in fade-in duration-1000">
            <div className="bg-white/5 border border-white/10 rounded-[3.5rem] overflow-hidden backdrop-blur-3xl shadow-2xl">

                {/* Header bar */}
                <div className="h-14 px-10 border-b border-white/10 bg-black/40 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className="font-mono text-[10px] font-black text-orange-400 tracking-[0.3em] uppercase">
                            ◆ MASTER TAPE · TAKE_0{selectedTakeIdx + 1}
                        </span>
                        <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="font-mono text-[9px] font-black text-emerald-400 uppercase tracking-widest">Broadcast Ready</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <MiniStepDashes current={4} />
                        <span className="text-[9px] font-black uppercase tracking-[0.3em] text-orange-400/50">MIX</span>
                    </div>
                </div>

                {/* Two-box grid — left fills full height via object-cover */}
                <div className="grid lg:grid-cols-[1fr_380px]">

                    {/* LEFT: Phone frame preview */}
                    <div className="relative overflow-hidden min-h-[580px] border-r border-white/10 flex items-center justify-center">

                        {/* Blurred ambient background */}
                        <div className="absolute inset-0">
                            <img src={image} alt="" aria-hidden className="w-full h-full object-cover scale-110" style={{ filter: `blur(28px) brightness(0.2) saturate(0.6)` }} />
                            <div className="absolute inset-0 bg-[#020617]/50" />
                        </div>

                        {/* Platform label */}
                        <div className="absolute top-5 left-6 z-10">
                            <span className="text-[8px] font-black uppercase tracking-[0.3em] text-white/20">
                                {aspectRatio === '9:16' ? 'Story / Reel' : aspectRatio === '1:1' ? 'Feed Post' : aspectRatio === '4:5' ? 'Portrait Post' : aspectRatio === '16:9' ? 'Landscape' : aspectRatio}
                            </span>
                        </div>

                        {/* Social / Device toggle */}
                        <div className="absolute top-4 right-4 z-30 flex bg-black/50 backdrop-blur-md border border-white/10 rounded-full p-0.5">
                            {[{ id: 'social', label: 'Social' }, { id: 'device', label: 'Device' }].map(m => (
                                <button
                                    key={m.id}
                                    onClick={() => setPreviewMode(m.id)}
                                    className={`px-3 py-1.5 rounded-full text-[8px] font-black uppercase tracking-wider transition-all ${previewMode === m.id ? 'bg-white/20 text-white shadow' : 'text-white/30 hover:text-white/60'}`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>

                        {/* Phone device frame */}
                        <div className={`relative z-10 my-10 bg-[#0a0a0a] shadow-[0_40px_120px_rgba(0,0,0,0.8)] flex flex-col ${
                            isLandscape
                                ? 'w-[460px] h-[240px] rounded-[2rem] border-[3px] border-white/12'
                                : 'w-[248px] h-[508px] rounded-[3.5rem] border-[3px] border-white/12'
                        }`}>

                            {/* Portrait: camera pill */}
                            {!isLandscape && (
                                <div className="absolute top-3.5 left-1/2 -translate-x-1/2 w-[68px] h-[22px] bg-black rounded-full z-20 border border-white/8 flex items-center justify-center gap-1.5">
                                    <div className="w-7 h-1.5 bg-white/10 rounded-full" />
                                    <div className="w-2 h-2 rounded-full bg-white/15" />
                                </div>
                            )}

                            {/* Landscape: side notch */}
                            {isLandscape && (
                                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[52px] bg-black rounded-full z-20 border border-white/8 flex items-center justify-center">
                                    <div className="w-1.5 h-7 bg-white/10 rounded-full" />
                                </div>
                            )}

                            {/* Screen */}
                            <div className={`flex-1 relative overflow-hidden ${isLandscape ? 'rounded-[1.6rem]' : 'rounded-[3.2rem]'}`}>

                                {previewMode === 'device' ? (
                                    /* ── DEVICE VIEW (Option C) ── */
                                    <>
                                        <img src={image} alt="Ad Preview"
                                            className={`absolute inset-0 w-full h-full ${isStoryFormat || isLandscape ? 'object-cover' : 'object-contain bg-black'}`}
                                            style={{ filter: filterCss, transform: `translate(${imgTransform.x/10}px, ${imgTransform.y/10}px) scale(${imgTransform.scale})` }}
                                        />
                                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/50 to-transparent pt-12 pb-4 px-4">
                                            <p className="text-[9px] font-medium text-white/75 leading-snug mb-2.5 line-clamp-2 drop-shadow">{text.split(' ').slice(0, 10).join(' ')}…</p>
                                            <div className="h-[2px] bg-white/20 rounded-full mb-2.5 overflow-hidden">
                                                <div className="h-full bg-white rounded-full transition-all duration-100" style={{ width: `${progress}%` }} />
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <PlayPill playing={isPlaying} onClick={() => setIsPlaying(p => !p)} size="sm" />
                                                <div className="flex-1" />
                                                <span className="font-mono text-[8px] text-white/50 font-black">{fmt(previewTime)} / {fmt(previewDuration)}</span>
                                            </div>
                                        </div>
                                    </>
                                ) : isStoryFormat ? (
                                    /* ── SOCIAL: Stories / Reels (9:16) ── */
                                    <>
                                        <img src={image} alt="Ad Preview"
                                            className="absolute inset-0 w-full h-full object-cover"
                                            style={{ filter: filterCss, transform: `translate(${imgTransform.x/10}px, ${imgTransform.y/10}px) scale(${imgTransform.scale})` }}
                                        />
                                        {/* Tap to play/pause */}
                                        <div className="absolute inset-0 z-10 cursor-pointer" onClick={() => setIsPlaying(p => !p)} />
                                        {/* Centered play indicator when paused */}
                                        {!isPlaying && (
                                            <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                                                <div className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center backdrop-blur-sm">
                                                    <div className="w-0 h-0 border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent border-l-[13px] border-l-white ml-0.5" />
                                                </div>
                                            </div>
                                        )}
                                        {/* Top chrome */}
                                        <div className="absolute top-0 inset-x-0 z-30 bg-gradient-to-b from-black/70 to-transparent pt-7 px-3 pb-6">
                                            {/* Story progress bars */}
                                            <div className="flex gap-1 mb-2.5">
                                                {[0, 1, 2].map(i => (
                                                    <div key={i} className="flex-1 h-[2px] bg-white/30 rounded-full overflow-hidden">
                                                        <div className="h-full bg-white rounded-full" style={{ width: i === 0 ? `${progress}%` : '0%', transition: i === 0 ? 'width 0.1s linear' : 'none' }} />
                                                    </div>
                                                ))}
                                            </div>
                                            {/* Profile row */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 border border-white/40 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-white text-[9px] font-black truncate leading-tight">{brandName}</p>
                                                    <p className="text-white/50 text-[7px] font-bold leading-tight">Sponsored</p>
                                                </div>
                                                <span className="text-white/60 text-xs font-black tracking-widest">···</span>
                                                <span className="text-white/60 text-[11px] ml-0.5">✕</span>
                                            </div>
                                        </div>
                                        {/* Right side actions */}
                                        <div className="absolute right-2 bottom-16 z-30 flex flex-col gap-3.5 items-center">
                                            {[
                                                { Icon: Heart,         label: '2.4K' },
                                                { Icon: MessageCircle, label: '183' },
                                                { Icon: Send,          label: null },
                                                { Icon: Bookmark,      label: null },
                                            ].map(({ Icon, label }) => (
                                                <div key={label ?? Icon.displayName} className="flex flex-col items-center gap-0.5">
                                                    <Icon className="w-5 h-5 text-white drop-shadow-md" strokeWidth={1.5} />
                                                    {label && <span className="text-white text-[7px] font-black drop-shadow">{label}</span>}
                                                </div>
                                            ))}
                                        </div>
                                        {/* Bottom chrome */}
                                        <div className="absolute bottom-0 inset-x-0 z-30 bg-gradient-to-t from-black/60 to-transparent pb-4 px-3 pt-6">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 flex items-center border border-white/40 rounded-full px-3 py-1.5">
                                                    <span className="text-white/40 text-[8px]">Send message</span>
                                                </div>
                                                <Heart className="w-4 h-4 text-white" strokeWidth={1.5} />
                                                <Send className="w-4 h-4 text-white" strokeWidth={1.5} />
                                            </div>
                                        </div>
                                    </>
                                ) : isFeedFormat ? (
                                    /* ── SOCIAL: Instagram Feed Post (1:1, 4:5) ── */
                                    <div className="absolute inset-0 overflow-y-auto bg-white flex flex-col">
                                        {/* Post header */}
                                        <div className="flex items-center gap-2 px-2.5 py-2 border-b border-gray-100 shrink-0">
                                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-black text-[8px] font-black truncate leading-tight">{brandName}</p>
                                                <p className="text-gray-400 text-[6px] font-bold leading-tight">Sponsored</p>
                                            </div>
                                            <span className="text-gray-400 text-[10px] tracking-widest">···</span>
                                        </div>
                                        {/* Image at native aspect ratio */}
                                        <div className="relative shrink-0 w-full" style={{ aspectRatio: aspectRatio.replace(':', '/') }}>
                                            <img src={image} className="w-full h-full object-cover cursor-pointer"
                                                style={{ filter: filterCss, transform: `translate(${imgTransform.x/10}px, ${imgTransform.y/10}px) scale(${imgTransform.scale})` }}
                                                onClick={() => setIsPlaying(p => !p)}
                                            />
                                            {!isPlaying && (
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                    <div className="w-9 h-9 rounded-full bg-black/40 flex items-center justify-center">
                                                        <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[11px] border-l-white ml-0.5" />
                                                    </div>
                                                </div>
                                            )}
                                            {/* Progress bar */}
                                            <div className="absolute bottom-0 inset-x-0 h-[2px] bg-black/20">
                                                <div className="h-full bg-indigo-500 transition-all duration-100" style={{ width: `${progress}%` }} />
                                            </div>
                                        </div>
                                        {/* Actions */}
                                        <div className="px-2.5 py-1.5 flex items-center gap-2.5 shrink-0">
                                            <Heart className="w-4 h-4 text-black" strokeWidth={1.5} />
                                            <MessageCircle className="w-4 h-4 text-black" strokeWidth={1.5} />
                                            <Send className="w-4 h-4 text-black rotate-12" strokeWidth={1.5} />
                                            <div className="flex-1" />
                                            <Bookmark className="w-4 h-4 text-black" strokeWidth={1.5} />
                                        </div>
                                        {/* Caption */}
                                        <div className="px-2.5 pb-2 shrink-0">
                                            <p className="text-[7px] text-black leading-snug">
                                                <span className="font-black">{brandName}</span>{' '}
                                                <span className="text-gray-600">{text.split(' ').slice(0, 8).join(' ')}…</span>
                                            </p>
                                        </div>
                                        {/* Learn More */}
                                        <div className="px-2.5 pb-3 shrink-0">
                                            <div className="border border-gray-200 rounded-lg py-1.5 text-center bg-gray-50">
                                                <span className="text-black text-[7px] font-black uppercase tracking-wider">Learn More</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* ── SOCIAL: Landscape / YouTube-style ── */
                                    <>
                                        <img src={image} alt="Ad Preview"
                                            className="absolute inset-0 w-full h-full object-cover"
                                            style={{ filter: filterCss, transform: `translate(${imgTransform.x/10}px, ${imgTransform.y/10}px) scale(${imgTransform.scale})` }}
                                        />
                                        <div className="absolute inset-0 cursor-pointer z-10" onClick={() => setIsPlaying(p => !p)} />
                                        {/* Ad badge */}
                                        <div className="absolute top-2 left-3 z-20 px-1.5 py-0.5 bg-yellow-400 rounded text-[6px] font-black text-black uppercase">Ad</div>
                                        {!isPlaying && (
                                            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                                                <div className="w-11 h-11 rounded-full bg-black/50 flex items-center justify-center">
                                                    <div className="w-0 h-0 border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent border-l-[13px] border-l-white ml-0.5" />
                                                </div>
                                            </div>
                                        )}
                                        {/* Progress bar */}
                                        <div className="absolute bottom-0 inset-x-0 z-20 h-1 bg-black/30">
                                            <div className="h-full bg-red-500 transition-all duration-100" style={{ width: `${progress}%` }} />
                                        </div>
                                        {/* Skip */}
                                        <div className="absolute bottom-3 right-2 z-20 px-2 py-1 bg-black/60 border border-white/20 rounded">
                                            <span className="text-[6px] font-black text-white/80 uppercase tracking-wider">Skip Ad ›</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Portrait: home indicator */}
                            {!isLandscape && (
                                <div className="h-6 bg-[#0a0a0a] flex items-center justify-center flex-shrink-0 rounded-b-[3.5rem]">
                                    <div className="w-20 h-[3px] bg-white/20 rounded-full" />
                                </div>
                            )}
                        </div>

                        <audio
                            ref={previewAudioRef}
                            src={activeTake?.url}
                            preload="auto"
                            onTimeUpdate={e => setPreviewTime(e.target.currentTime)}
                            onLoadedMetadata={e => setPreviewDuration(e.target.duration)}
                            onEnded={() => setIsPlaying(false)}
                        />
                    </div>

                    {/* RIGHT: Take details + Export */}
                    <div className="flex flex-col">

                        {/* THE TAKE — scrollable script */}
                        <div className="flex-1 p-8 flex flex-col gap-5 overflow-hidden">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400">◆ THE TAKE</label>

                            {/* Take identity */}
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 rounded-full bg-orange-500 flex items-center justify-center font-black text-white text-base shadow-lg shadow-orange-500/20 flex-shrink-0">
                                    {selectedTakeIdx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-black text-white text-sm">{voiceLabel}</p>
                                        <span className="text-slate-700 text-xs">·</span>
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{genderLabel}</span>
                                        {scriptBadge && (
                                            <span className={`px-2 py-0.5 rounded border text-[8px] font-black uppercase tracking-widest ${scriptBadge.cls}`}>
                                                {scriptBadge.label}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">
                                        {previewDuration ? previewDuration.toFixed(1) + 's' : '—'} · {text.split(' ').filter(Boolean).length}w
                                    </p>
                                </div>
                            </div>

                            {/* Scrollable script */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar bg-black/20 rounded-2xl p-5 border border-white/5 min-h-[160px]">
                                <p className="text-[13px] text-white/75 leading-relaxed font-medium whitespace-pre-wrap">
                                    {text || '—'}
                                </p>
                            </div>
                        </div>

                        {/* EXPORT */}
                        <div className="p-8 border-t border-white/10 bg-black/20 space-y-5">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 block">◆ EXPORT</label>

                            {/* Radio: Video / Audio */}
                            <div className="flex gap-8">
                                {[
                                    { id: 'video', label: 'Video', sub: 'WebM · 1080p' },
                                    { id: 'audio', label: 'Audio Only', sub: 'WAV · 24-bit' },
                                ].map(opt => (
                                    <button
                                        key={opt.id}
                                        onClick={() => { setExportType(opt.id); setVideoReady(null); }}
                                        className="flex items-center gap-2.5 group"
                                    >
                                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${exportType === opt.id ? 'border-indigo-400' : 'border-white/20 group-hover:border-white/40'}`}>
                                            {exportType === opt.id && <span className="w-2 h-2 rounded-full bg-indigo-400 block" />}
                                        </span>
                                        <div className="text-left">
                                            <p className={`text-[11px] font-black uppercase tracking-wider ${exportType === opt.id ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'}`}>
                                                {opt.label}
                                            </p>
                                            <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">{opt.sub}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Download button */}
                            <button
                                onClick={handleDownload}
                                disabled={isRendering || !activeTake}
                                className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 disabled:opacity-40 flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/20"
                            >
                                {isRendering ? (
                                    <><RefreshCw className="w-4 h-4 animate-spin" /> Rendering…</>
                                ) : exportType === 'video' ? (
                                    <><Video className="w-4 h-4" /> {videoReady ? 'Download Again' : 'Download Video'}</>
                                ) : (
                                    <><FileAudio className="w-4 h-4" /> Download Audio</>
                                )}
                            </button>

                            {/* New project */}
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full py-3 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-white hover:bg-white/10 transition-all"
                            >
                                + Create New Ad
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Back link */}
            <div className="mt-8 flex justify-center">
                <button
                    onClick={() => setStep(2)}
                    className="flex items-center gap-2 text-slate-600 hover:text-white transition-all text-[10px] font-black uppercase tracking-[0.2em]"
                >
                    <ChevronLeft className="w-4 h-4" /> Adjust script or voice
                </button>
            </div>
        </div>
    );
};

export default Step3_MasterTape;
